import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'

export function useAdminDashboard(range = '30d') {
  return useQuery({
    queryKey: ['admin', 'dashboard', range],
    queryFn: () => api.get(`/admin/dashboard?range=${range}`),
    staleTime: 5 * 60 * 1000,
  })
}

export function useAdminPropsForGame(gameId) {
  return useQuery({
    queryKey: ['adminProps', gameId],
    queryFn: () => api.get(`/admin/props/game/${gameId}`),
    enabled: !!gameId,
  })
}

export function useAdminFeaturedProps() {
  return useQuery({
    queryKey: ['adminFeaturedProps'],
    queryFn: () => api.get('/admin/props/featured'),
  })
}

export function useSyncProps() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ gameId, markets }) =>
      api.post('/admin/props/sync', { gameId, markets }),
    onSuccess: (_, { gameId }) => {
      queryClient.invalidateQueries({ queryKey: ['adminProps', gameId] })
    },
  })
}

export function useFeatureProp() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ propId, featuredDate }) =>
      api.post('/admin/props/feature', { propId, featuredDate }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminProps'] })
      queryClient.invalidateQueries({ queryKey: ['adminFeaturedProps'] })
      queryClient.invalidateQueries({ queryKey: ['featuredProps'] })
    },
  })
}

export function useUnfeatureProp() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (propId) => api.post(`/admin/props/${propId}/unfeature`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminProps'] })
      queryClient.invalidateQueries({ queryKey: ['adminFeaturedProps'] })
      queryClient.invalidateQueries({ queryKey: ['featuredProps'] })
    },
  })
}

export function useVoidProp() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (propId) => api.post(`/admin/props/${propId}/void`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminProps'] })
      queryClient.invalidateQueries({ queryKey: ['adminFeaturedProps'] })
      queryClient.invalidateQueries({ queryKey: ['featuredProps'] })
      queryClient.invalidateQueries({ queryKey: ['propPicks'] })
    },
  })
}

export function useSettleProps() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (settlements) => api.post('/admin/props/settle', { settlements }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminProps'] })
      queryClient.invalidateQueries({ queryKey: ['adminFeaturedProps'] })
      queryClient.invalidateQueries({ queryKey: ['featuredProps'] })
      queryClient.invalidateQueries({ queryKey: ['propPicks'] })
    },
  })
}

export function useSyncOdds() {
  return useMutation({
    mutationFn: () => api.post('/admin/sync-odds'),
  })
}

export function useSyncInjuries() {
  return useMutation({
    mutationFn: () => api.post('/admin/sync-injuries'),
  })
}

export function useScoreGames() {
  return useMutation({
    mutationFn: () => api.post('/admin/score-games'),
  })
}

export function useRecalculatePoints() {
  return useMutation({
    mutationFn: () => api.post('/admin/recalculate-points'),
  })
}

export function useSyncNflPlayers() {
  return useMutation({
    mutationFn: () => api.post('/admin/fantasy/sync-players'),
  })
}

export function useSyncNBASalaries() {
  return useMutation({
    mutationFn: (date) => api.post('/admin/nba-dfs/generate-salaries', { date, season: 2026 }),
  })
}

export function useSyncMLBSalaries() {
  return useMutation({
    mutationFn: (date) => api.post('/admin/mlb-dfs/generate-salaries', { date, season: 2026 }),
  })
}

export function useSyncWNBASalaries() {
  return useMutation({
    mutationFn: (date) => api.post('/admin/wnba-dfs/generate-salaries', { date, season: 2026 }),
  })
}

export function useSyncNFLSalaries() {
  return useMutation({
    mutationFn: ({ week, season }) => api.post('/admin/dfs/generate-salaries', { week, season }),
  })
}

// Current NFL week + season from our nfl_schedule. The salary editor
// opens on this rather than a hardcoded Week 1 — see the note on
// GET /admin/dfs/current-week.
export function useAdminCurrentNflWeek() {
  return useQuery({
    queryKey: ['admin', 'dfs-current-week'],
    queryFn: () => api.get('/admin/dfs/current-week'),
    staleTime: 5 * 60_000,
  })
}

// Publish draft rows for a (week, season) so users can see the pool.
export function usePublishNFLSalaries() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ week, season }) => api.post('/admin/dfs/publish-salaries', { week, season }),
    onSuccess: () => {
      // Must match the query's real key: ['admin', 'dfs-salaries', week,
      // season, position, search]. 'adminDFSSalaries' was never a query key
      // anywhere — it only appeared in these two invalidations, so the POST
      // succeeded server-side and the table never refetched. The hide toggle
      // looked completely dead as a result.
      queryClient.invalidateQueries({ queryKey: ['admin', 'dfs-salaries'] })
      queryClient.invalidateQueries({ queryKey: ['adminDFSUnpublishedCount'] })
    },
  })
}

// Toggle whether the salary row appears in the user pool. Independent
// of pricing / manual override — used to bench third-string QBs,
// bye-week players (auto-hidden by generateSalaries), etc.
export function useToggleDFSHidden() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, hidden }) => api.post(`/admin/dfs/salaries/${id}/hide`, { hidden }),
    onSuccess: () => {
      // Must match the query's real key: ['admin', 'dfs-salaries', week,
      // season, position, search]. 'adminDFSSalaries' was never a query key
      // anywhere — it only appeared in these two invalidations, so the POST
      // succeeded server-side and the table never refetched. The hide toggle
      // looked completely dead as a result.
      queryClient.invalidateQueries({ queryKey: ['admin', 'dfs-salaries'] })
    },
  })
}

// Draft-row count for the currently-selected (week, season). Powers
// the "N unpublished" status label + Publish button visibility.
export function useAdminDFSUnpublishedCount({ week, season }) {
  return useQuery({
    queryKey: ['adminDFSUnpublishedCount', week, season],
    queryFn: () => api.get(`/admin/dfs/unpublished-count?week=${week}&season=${season}`),
    enabled: !!week && !!season,
    staleTime: 15 * 1000,
  })
}

export function useEnrichEspnIds() {
  return useMutation({
    mutationFn: () => api.post('/admin/fantasy/enrich-espn-ids'),
  })
}

export function useSyncWeeklyProjections() {
  return useMutation({
    mutationFn: ({ week, season }) => api.post('/admin/fantasy/sync-weekly-projections', { week, season }),
  })
}

export function useAdminDFSSalaries({ week, season, position = 'ALL', search = '' }) {
  return useQuery({
    queryKey: ['admin', 'dfs-salaries', week, season, position, search],
    queryFn: () => {
      const params = new URLSearchParams({ week: String(week), season: String(season), position })
      if (search) params.set('search', search)
      return api.get(`/admin/dfs/salaries?${params.toString()}`)
    },
    enabled: Number.isInteger(week) && Number.isInteger(season),
    staleTime: 30_000,
  })
}

export function useUpdateDFSSalary() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, salary }) => api.patch(`/admin/dfs/salaries/${id}`, { salary }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'dfs-salaries'] }),
  })
}

export function useResetDFSSalary() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id) => api.post(`/admin/dfs/salaries/${id}/reset`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'dfs-salaries'] }),
  })
}

export function useAdminWnbaDfsSalaries({ date, season, position = 'ALL', search = '' }) {
  return useQuery({
    queryKey: ['admin', 'wnba-dfs-salaries', date, season, position, search],
    queryFn: () => {
      const params = new URLSearchParams({ date, season: String(season), position })
      if (search) params.set('search', search)
      return api.get(`/admin/wnba-dfs/salaries?${params.toString()}`)
    },
    enabled: !!date && Number.isInteger(season),
    staleTime: 30_000,
  })
}

export function useUpdateWnbaDfsSalary() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, salary }) => api.patch(`/admin/wnba-dfs/salaries/${id}`, { salary }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'wnba-dfs-salaries'] }),
  })
}

export function useResetWnbaDfsSalary() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id }) => api.post(`/admin/wnba-dfs/salaries/${id}/reset`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'wnba-dfs-salaries'] }),
  })
}

export function useAdminNbaDfsSalaries({ date, season, position = 'ALL', search = '' }) {
  return useQuery({
    queryKey: ['admin', 'nba-dfs-salaries', date, season, position, search],
    queryFn: () => {
      const params = new URLSearchParams({ date, season: String(season), position })
      if (search) params.set('search', search)
      return api.get(`/admin/nba-dfs/salaries?${params.toString()}`)
    },
    enabled: !!date && Number.isInteger(season),
    staleTime: 30_000,
  })
}

export function useUpdateNbaDfsSalary() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, salary }) => api.patch(`/admin/nba-dfs/salaries/${id}`, { salary }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'nba-dfs-salaries'] }),
  })
}

export function useResetNbaDfsSalary() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id }) => api.post(`/admin/nba-dfs/salaries/${id}/reset`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'nba-dfs-salaries'] }),
  })
}

export function useAdminMlbDfsSalaries({ date, season, position = 'ALL', search = '' }) {
  return useQuery({
    queryKey: ['admin', 'mlb-dfs-salaries', date, season, position, search],
    queryFn: () => {
      const params = new URLSearchParams({ date, season: String(season), position })
      if (search) params.set('search', search)
      return api.get(`/admin/mlb-dfs/salaries?${params.toString()}`)
    },
    enabled: !!date && Number.isInteger(season),
    staleTime: 30_000,
  })
}

export function useUpdateMlbDfsSalary() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, salary }) => api.patch(`/admin/mlb-dfs/salaries/${id}`, { salary }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'mlb-dfs-salaries'] }),
  })
}

export function useResetMlbDfsSalary() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id }) => api.post(`/admin/mlb-dfs/salaries/${id}/reset`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'mlb-dfs-salaries'] }),
  })
}

export function useRecalculateRecords() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => api.post('/admin/recalculate-records'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['records'] })
      queryClient.invalidateQueries({ queryKey: ['royalty'] })
    },
  })
}

export function useSendEmailBlast() {
  const qc = useQueryClient()
  return useMutation({
    // Email blast to every subscribed user loops one SMTP send per
    // recipient and can easily exceed 30s — use longTimeout so the
    // client waits rather than reporting failure on a still-running
    // request.
    mutationFn: ({ subject, body, scheduled_at }) =>
      api.post('/admin/email-blast', { subject, body, scheduled_at }, { longTimeout: true }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'email-logs'] }),
  })
}

export function useSendTargetedEmail() {
  const qc = useQueryClient()
  return useMutation({
    // Targeted sends can also run long (one SMTP call per recipient).
    // The default 30s client timeout was surfacing false 'timed out'
    // errors while the server was still processing.
    mutationFn: ({ subject, body, usernames, scheduled_at }) =>
      api.post('/admin/email-targeted', { subject, body, usernames, scheduled_at }, { longTimeout: true }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'email-logs'] }),
  })
}

export function useEmailLogs() {
  return useQuery({
    queryKey: ['admin', 'email-logs'],
    queryFn: () => api.get('/admin/email-logs'),
  })
}

export function useAdminLeagueSearch(query) {
  return useQuery({
    queryKey: ['admin', 'league-search', query],
    queryFn: () => api.get(`/admin/leagues/search?q=${encodeURIComponent(query)}`),
    enabled: query?.length >= 2,
  })
}

// User Lookup
export function useAdminUserLookup(userId) {
  return useQuery({
    queryKey: ['admin', 'user-lookup', userId],
    queryFn: () => api.get(`/admin/users/lookup?user_id=${userId}`),
    enabled: !!userId,
  })
}

export function useAdminSubscriptionOverride() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data) => api.post('/admin/users/subscription', data),
    onSuccess: (_, variables) => qc.invalidateQueries({ queryKey: ['admin', 'user-lookup', variables.user_id] }),
  })
}

export function useAdminResetPassword() {
  return useMutation({
    mutationFn: ({ user_id, password }) => api.post(`/admin/users/${user_id}/set-password`, { password }),
  })
}

// Game Override
export function useAdminGameSearch(query) {
  return useQuery({
    queryKey: ['admin', 'game-search', query],
    queryFn: () => api.get(`/admin/games/search?q=${encodeURIComponent(query)}`),
    enabled: query?.length >= 2,
  })
}

export function useAdminGameOverride() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data) => api.post('/admin/games/override', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'game-search'] }),
  })
}

// Futures
export function useSyncFutures() {
  return useMutation({
    mutationFn: () => api.post('/admin/futures/sync-all'),
  })
}

export function useAdminFuturesMarkets(sport) {
  return useQuery({
    queryKey: ['adminFuturesMarkets', sport],
    queryFn: () => api.get(`/admin/futures/markets${sport ? `?sport=${sport}` : ''}`),
  })
}

export function useCloseFuturesMarket() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (marketId) => api.post(`/admin/futures/markets/${marketId}/close`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminFuturesMarkets'] })
      queryClient.invalidateQueries({ queryKey: ['futuresMarkets'] })
    },
  })
}

export function useSettleFuturesMarket() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ marketId, winningOutcome }) =>
      api.post('/admin/futures/settle', { marketId, winningOutcome }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminFuturesMarkets'] })
      queryClient.invalidateQueries({ queryKey: ['futuresMarkets'] })
      queryClient.invalidateQueries({ queryKey: ['futuresPicks'] })
    },
  })
}

export function useCreateFuturesMarket() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data) => api.post('/admin/futures/create', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminFuturesMarkets'] })
      queryClient.invalidateQueries({ queryKey: ['futuresMarkets'] })
    },
  })
}

export function useUpdateFuturesMarket() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ marketId, ...rest }) =>
      api.patch(`/admin/futures/markets/${marketId}`, rest),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminFuturesMarkets'] })
      queryClient.invalidateQueries({ queryKey: ['futuresMarkets'] })
    },
  })
}

// Trigger stat-leader auto-resolution for a single market.
export function useResolveStatLeaderFuture() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (marketId) => api.post(`/admin/futures/markets/${marketId}/resolve-stat-leader`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminFuturesMarkets'] })
      queryClient.invalidateQueries({ queryKey: ['futuresMarkets'] })
      queryClient.invalidateQueries({ queryKey: ['futuresPicks'] })
    },
  })
}

export function useResolveTeamWinTotalFuture() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (marketId) => api.post(`/admin/futures/markets/${marketId}/resolve-team-win-total`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminFuturesMarkets'] })
      queryClient.invalidateQueries({ queryKey: ['futuresMarkets'] })
      queryClient.invalidateQueries({ queryKey: ['futuresPicks'] })
    },
  })
}

export function useResolvePlayerStatOverUnderFuture() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (marketId) => api.post(`/admin/futures/markets/${marketId}/resolve-player-stat-over-under`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminFuturesMarkets'] })
      queryClient.invalidateQueries({ queryKey: ['futuresMarkets'] })
      queryClient.invalidateQueries({ queryKey: ['futuresPicks'] })
    },
  })
}

// Team names for bracket autocomplete
export function useTeamsForSport(sport) {
  return useQuery({
    queryKey: ['adminTeams', sport],
    queryFn: () => api.get(`/admin/teams?sport=${sport}`),
    enabled: !!sport,
  })
}

// Bracket Templates
export function useBracketTemplates(sport) {
  return useQuery({
    queryKey: ['adminBracketTemplates', sport],
    queryFn: () => api.get(`/admin/bracket-templates${sport ? `?sport=${sport}` : ''}`),
  })
}

export function useBracketTemplate(templateId) {
  return useQuery({
    queryKey: ['adminBracketTemplates', templateId],
    queryFn: () => api.get(`/admin/bracket-templates/${templateId}`),
    enabled: !!templateId,
  })
}

export function useCreateBracketTemplate() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data) => api.post('/admin/bracket-templates', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminBracketTemplates'] })
      queryClient.invalidateQueries({ queryKey: ['bracketTemplates'] })
    },
  })
}

export function useUpdateBracketTemplate() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ templateId, ...data }) => api.patch(`/admin/bracket-templates/${templateId}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminBracketTemplates'] })
      queryClient.invalidateQueries({ queryKey: ['bracketTemplates'] })
    },
  })
}

export function useSaveBracketTemplateMatchups() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ templateId, matchups }) =>
      api.post(`/admin/bracket-templates/${templateId}/matchups`, { matchups }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminBracketTemplates'] })
    },
  })
}

export function useDeleteBracketTemplate() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (templateId) => api.delete(`/admin/bracket-templates/${templateId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminBracketTemplates'] })
      queryClient.invalidateQueries({ queryKey: ['bracketTemplates'] })
    },
  })
}

export function useEnterTemplateResult() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ templateId, templateMatchupId, winner, scoreTop, scoreBottom, seriesWinsTop, seriesWinsBottom }) =>
      api.post(`/admin/bracket-templates/${templateId}/results`, {
        template_matchup_id: templateMatchupId,
        winner,
        score_top: scoreTop != null ? Number(scoreTop) : undefined,
        score_bottom: scoreBottom != null ? Number(scoreBottom) : undefined,
        series_wins_top: seriesWinsTop != null ? Number(seriesWinsTop) : undefined,
        series_wins_bottom: seriesWinsBottom != null ? Number(seriesWinsBottom) : undefined,
      }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['adminBracketTemplates', variables.templateId] })
    },
  })
}

export function useUndoTemplateResult() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ templateId, templateMatchupId }) =>
      api.delete(`/admin/bracket-templates/${templateId}/results/${templateMatchupId}`),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['adminBracketTemplates', variables.templateId] })
    },
  })
}

// Bracket Championship Score
export function useSetChampionshipScore() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ templateId, totalScore }) =>
      api.post(`/admin/bracket-templates/${templateId}/championship-score`, { total_score: totalScore }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['adminBracketTemplates', variables.templateId] })
    },
  })
}

// Template Bracket Email
export function useSendTemplateBracketEmail() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ subject, body, templateId, scheduled_at }) =>
      api.post('/admin/email-template-blast', { subject, body, templateId, scheduled_at }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'email-logs'] }),
  })
}

export function useBracketTemplateUserCount(templateId) {
  return useQuery({
    queryKey: ['admin', 'bracket-template-user-count', templateId],
    queryFn: () => api.get(`/admin/bracket-templates/${templateId}/user-count`),
    enabled: !!templateId,
  })
}

// Content Moderation
export function useBannedWords() {
  return useQuery({
    queryKey: ['admin', 'banned-words'],
    queryFn: () => api.get('/admin/banned-words'),
  })
}

export function useAddBannedWord() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (word) => api.post('/admin/banned-words', { word }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'banned-words'] }),
  })
}

export function useRemoveBannedWord() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id) => api.delete(`/admin/banned-words/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'banned-words'] }),
  })
}

export function useMutedUsers() {
  return useQuery({
    queryKey: ['admin', 'muted-users'],
    queryFn: () => api.get('/admin/muted-users'),
  })
}

export function useMuteUser() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (userId) => api.post(`/admin/users/${userId}/mute`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'muted-users'] }),
  })
}

export function useUnmuteUser() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (userId) => api.post(`/admin/users/${userId}/unmute`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'muted-users'] }),
  })
}

// Player Search (for position overrides)
// sportKey is part of the key AND the request — without it the endpoint
// only ever searched NBA, so picking NFL in the panel returned nothing.
export function useAdminPlayerSearch(query, sportKey) {
  return useQuery({
    queryKey: ['admin', 'player-search', query, sportKey],
    queryFn: () => api.get(`/admin/player-search?q=${encodeURIComponent(query)}&sport=${encodeURIComponent(sportKey || '')}`),
    enabled: !!query && query.length >= 2,
    staleTime: 30_000,
  })
}

// Player Position Overrides
// Scoped to the panel's selected sport so NBA overrides don't linger on
// screen while NFL is selected. Omitting sportKey returns all sports.
export function usePlayerPositionOverrides(sportKey) {
  return useQuery({
    queryKey: ['admin', 'position-overrides', sportKey],
    queryFn: () => api.get(`/admin/player-position-overrides${sportKey ? `?sport=${encodeURIComponent(sportKey)}` : ''}`),
  })
}

export function useCreatePositionOverride() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data) => api.post('/admin/player-position-overrides', data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'position-overrides'] }),
  })
}

export function useDeletePositionOverride() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id) => api.delete(`/admin/player-position-overrides/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'position-overrides'] }),
  })
}

// Admin pending counts (badge indicators)
export function useAdminPendingCounts() {
  return useQuery({
    queryKey: ['admin', 'pending-counts'],
    queryFn: () => api.get('/admin/pending-counts'),
    refetchInterval: 60_000, // poll every minute
  })
}

// Detail on leagues that should have completed but haven't — used by the
// Dashboard's Stuck Leagues card. Includes the game rows blocking each so
// an admin can jump straight to Game Override with the id in hand.
export function useStuckLeagues() {
  return useQuery({
    queryKey: ['admin', 'stuck-leagues'],
    queryFn: () => api.get('/admin/stuck-leagues'),
    refetchInterval: 120_000,
  })
}

// Backdrop submissions
export function useBackdropSubmissions() {
  return useQuery({
    queryKey: ['admin', 'backdrop-submissions'],
    queryFn: () => api.get('/admin/backdrop-submissions'),
  })
}

export function useApproveBackdrop() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id) => api.post(`/admin/backdrop-submissions/${id}/approve`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'backdrop-submissions'] }),
  })
}

export function useRejectBackdrop() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, reason }) => api.post(`/admin/backdrop-submissions/${id}/reject`, { reason }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'backdrop-submissions'] }),
  })
}

export function useAdminSurveyLeagues() {
  return useQuery({
    queryKey: ['admin', 'surveys', 'leagues'],
    queryFn: () => api.get('/admin/surveys/leagues'),
    staleTime: 30 * 1000,
  })
}

export function useDesignateSurvey() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ league_id, enabled }) => api.post('/admin/surveys/designate', { league_id, enabled }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'surveys'] }),
  })
}

export function useAdminSurveyResponses(leagueId) {
  return useQuery({
    queryKey: ['admin', 'surveys', 'responses', leagueId],
    queryFn: () => api.get(`/admin/surveys/responses?league_id=${leagueId}`),
    enabled: !!leagueId,
  })
}
